import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Plus, X } from "lucide-react";
import { adminApi, type AdminPlan } from "@/api/admin";

export const Route = createFileRoute("/admin/plans")({
  component: AdminPlansPage,
});

function blankPlan(): AdminPlan {
  return {
    id: "",
    display_name: "",
    monthly_price_usd: "0",
    monthly_credits: "0",
    cycle_days: 30,
    rps_limit: 0,
    overage_credit_price_usd: "0",
    sse_connections_max: 0,
    max_tokens_per_sse_conn: 0,
    hidden: false,
    sort_order: 0,
  };
}

function AdminPlansPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-plans"],
    queryFn: () => adminApi.listAllPlans(),
  });
  const [adding, setAdding] = useState(false);

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof adminApi.updatePlan>[1] }) =>
      adminApi.updatePlan(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-plans"] }),
  });
  const create = useMutation({
    mutationFn: (plan: AdminPlan) => adminApi.createPlan(plan),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-plans"] });
      setAdding(false);
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-6">
        <div>
          <h1 className="text-2xl font-semibold">Plans</h1>
          <p className="mt-1 text-ink/65">
            Edit display order, id, price, credit limits, SSE caps, and visibility.
            Edits apply to new signups + future plan changes. Existing customers
            keep their snapshotted terms (Pattern A) — to push a price change to
            current subscribers, update the projects table separately.
          </p>
          <p className="mt-2 text-sm text-ink/65 text-pretty">
            <span className="font-medium text-ink">Overage $/cu</span> encoding:{" "}
            <code className="rounded bg-ink/5 px-1.5 py-0.5 font-mono text-xs text-ink">-1</code> = no cap (always allow overage),{" "}
            <code className="rounded bg-ink/5 px-1.5 py-0.5 font-mono text-xs text-ink">0</code> = hard cap (return{" "}
            <code className="font-mono text-xs">429 quota_exhausted</code> once the cycle limit is hit),{" "}
            <code className="rounded bg-ink/5 px-1.5 py-0.5 font-mono text-xs text-ink">&gt;0</code> = soft cap (overage billed at $X per credit).
          </p>
        </div>
        <button className="btn-primary shrink-0" onClick={() => setAdding(true)} disabled={adding}>
          <Plus size={16} className="mr-1" /> New plan
        </button>
      </div>

      <div className="card overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="bg-cream text-left text-xs uppercase tracking-wide text-ink/55">
            <tr>
              <th className="px-4 py-3">Order</th>
              <th className="px-4 py-3">ID</th>
              <th className="px-4 py-3">Display name</th>
              <th className="px-4 py-3 text-right">Price (USD)</th>
              <th className="px-4 py-3 text-right">Credits</th>
              <th className="px-4 py-3 text-right">Cycle (d)</th>
              <th className="px-4 py-3 text-right">RPS</th>
              <th className="px-4 py-3 text-right">SSE conn.</th>
              <th className="px-4 py-3 text-right">Tokens/SSE</th>
              <th className="px-4 py-3 text-right">Overage $/cu</th>
              <th className="px-4 py-3">Hidden</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {adding && (
              <NewPlanRow
                onCancel={() => setAdding(false)}
                onSave={(plan) => create.mutate(plan)}
                saving={create.isPending}
              />
            )}
            {isLoading ? (
              <tr><td colSpan={12} className="px-4 py-6 text-ink/55">Loading…</td></tr>
            ) : data?.plans.map((p) => (
              <PlanRow key={p.id} plan={p} onSave={(patch) => update.mutate({ id: p.id, patch })} saving={update.isPending} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// All editable fields share the same input layout — keeps the row tight.
type Draft = {
  id: string;
  display_name: string;
  monthly_price_usd: string;
  monthly_credits: string;
  cycle_days: number;
  rps_limit: number;
  overage_credit_price_usd: string;
  sse_connections_max: number;
  max_tokens_per_sse_conn: number;
  hidden: boolean;
  sort_order: number;
};

function PlanRow({
  plan,
  onSave,
  saving,
}: {
  plan: AdminPlan;
  onSave: (patch: Parameters<typeof adminApi.updatePlan>[1]) => void;
  saving: boolean;
}) {
  const [draft, setDraft] = useState<Draft>({
    id: plan.id,
    display_name: plan.display_name,
    monthly_price_usd: plan.monthly_price_usd,
    monthly_credits: plan.monthly_credits,
    cycle_days: plan.cycle_days,
    rps_limit: plan.rps_limit,
    overage_credit_price_usd: plan.overage_credit_price_usd,
    sse_connections_max: plan.sse_connections_max,
    max_tokens_per_sse_conn: plan.max_tokens_per_sse_conn,
    hidden: plan.hidden,
    sort_order: plan.sort_order,
  });

  const dirty =
    draft.id !== plan.id ||
    draft.display_name !== plan.display_name ||
    draft.monthly_price_usd !== plan.monthly_price_usd ||
    draft.monthly_credits !== plan.monthly_credits ||
    draft.cycle_days !== plan.cycle_days ||
    draft.rps_limit !== plan.rps_limit ||
    draft.overage_credit_price_usd !== plan.overage_credit_price_usd ||
    draft.sse_connections_max !== plan.sse_connections_max ||
    draft.max_tokens_per_sse_conn !== plan.max_tokens_per_sse_conn ||
    draft.hidden !== plan.hidden ||
    draft.sort_order !== plan.sort_order;

  function save() {
    const patch: Parameters<typeof adminApi.updatePlan>[1] = {};
    if (draft.id !== plan.id) patch.id = draft.id;
    if (draft.display_name !== plan.display_name) patch.display_name = draft.display_name;
    if (draft.monthly_price_usd !== plan.monthly_price_usd) patch.monthly_price_usd = draft.monthly_price_usd;
    if (draft.monthly_credits !== plan.monthly_credits) patch.monthly_credits = draft.monthly_credits;
    if (draft.cycle_days !== plan.cycle_days) patch.cycle_days = draft.cycle_days;
    if (draft.rps_limit !== plan.rps_limit) patch.rps_limit = draft.rps_limit;
    if (draft.overage_credit_price_usd !== plan.overage_credit_price_usd) patch.overage_credit_price_usd = draft.overage_credit_price_usd;
    if (draft.sse_connections_max !== plan.sse_connections_max) patch.sse_connections_max = draft.sse_connections_max;
    if (draft.max_tokens_per_sse_conn !== plan.max_tokens_per_sse_conn) patch.max_tokens_per_sse_conn = draft.max_tokens_per_sse_conn;
    if (draft.hidden !== plan.hidden) patch.hidden = draft.hidden;
    if (draft.sort_order !== plan.sort_order) patch.sort_order = draft.sort_order;
    onSave(patch);
  }

  return (
    <tr className="border-t border-ink/8">
      <RowInputs draft={draft} setDraft={setDraft} />
      <td className="px-4 py-2 text-right">
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="btn-primary text-xs disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? "…" : "Save"}
        </button>
      </td>
    </tr>
  );
}

function NewPlanRow({
  onCancel, onSave, saving,
}: {
  onCancel: () => void;
  onSave: (plan: AdminPlan) => void;
  saving: boolean;
}) {
  const [draft, setDraft] = useState<Draft>(() => blankPlan() as unknown as Draft);
  const ready = draft.id.trim().length > 0 && draft.display_name.trim().length > 0;

  function save() {
    onSave({
      id: draft.id.trim(),
      display_name: draft.display_name.trim(),
      monthly_price_usd: draft.monthly_price_usd,
      monthly_credits: draft.monthly_credits,
      cycle_days: draft.cycle_days,
      rps_limit: draft.rps_limit,
      overage_credit_price_usd: draft.overage_credit_price_usd,
      sse_connections_max: draft.sse_connections_max,
      max_tokens_per_sse_conn: draft.max_tokens_per_sse_conn,
      hidden: draft.hidden,
      sort_order: draft.sort_order,
    });
  }

  return (
    <tr className="border-t border-ink/8 bg-peach-500/5">
      <RowInputs draft={draft} setDraft={setDraft} />
      <td className="px-4 py-2 text-right">
        <div className="inline-flex items-center gap-1">
          <button
            onClick={onCancel}
            className="rounded px-2 py-1 text-xs text-ink/65 hover:bg-ink/5"
            title="Cancel"
          >
            <X size={14} />
          </button>
          <button
            onClick={save}
            disabled={!ready || saving}
            className="btn-primary text-xs disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "…" : "Create"}
          </button>
        </div>
      </td>
    </tr>
  );
}

// Shared cell layout for create + edit. Keeps the row markup in one place
// so the columns line up between the new-row and existing rows.
function RowInputs({
  draft, setDraft,
}: {
  draft: Draft;
  setDraft: (d: Draft) => void;
}) {
  const num = (v: string) => Number(v);
  return (
    <>
      <td className="px-4 py-2">
        <input
          type="number"
          className="w-16 rounded border border-ink/15 px-2 py-1 text-sm"
          value={draft.sort_order}
          onChange={(e) => setDraft({ ...draft, sort_order: num(e.target.value) })}
        />
      </td>
      <td className="px-4 py-2">
        <input
          type="text"
          className="w-32 rounded border border-ink/15 px-2 py-1 font-mono text-xs"
          value={draft.id}
          placeholder="e.g. pro"
          onChange={(e) => setDraft({ ...draft, id: e.target.value })}
        />
      </td>
      <td className="px-4 py-2">
        <input
          type="text"
          className="w-32 rounded border border-ink/15 px-2 py-1 text-sm"
          value={draft.display_name}
          onChange={(e) => setDraft({ ...draft, display_name: e.target.value })}
        />
      </td>
      <td className="px-4 py-2 text-right">
        <input
          type="text"
          className="w-20 rounded border border-ink/15 px-2 py-1 text-right text-sm"
          value={draft.monthly_price_usd}
          onChange={(e) => setDraft({ ...draft, monthly_price_usd: e.target.value })}
        />
      </td>
      <td className="px-4 py-2 text-right">
        <input
          type="text"
          className="w-28 rounded border border-ink/15 px-2 py-1 text-right text-sm"
          value={draft.monthly_credits}
          onChange={(e) => setDraft({ ...draft, monthly_credits: e.target.value })}
        />
      </td>
      <td className="px-4 py-2 text-right">
        <input
          type="number"
          className="w-16 rounded border border-ink/15 px-2 py-1 text-right text-sm"
          value={draft.cycle_days}
          onChange={(e) => setDraft({ ...draft, cycle_days: num(e.target.value) })}
        />
      </td>
      <td className="px-4 py-2 text-right">
        <input
          type="number"
          className="w-16 rounded border border-ink/15 px-2 py-1 text-right text-sm"
          value={draft.rps_limit}
          onChange={(e) => setDraft({ ...draft, rps_limit: num(e.target.value) })}
        />
      </td>
      <td className="px-4 py-2 text-right">
        <input
          type="number"
          className="w-16 rounded border border-ink/15 px-2 py-1 text-right text-sm"
          value={draft.sse_connections_max}
          onChange={(e) => setDraft({ ...draft, sse_connections_max: num(e.target.value) })}
        />
      </td>
      <td className="px-4 py-2 text-right">
        <input
          type="number"
          className="w-16 rounded border border-ink/15 px-2 py-1 text-right text-sm"
          value={draft.max_tokens_per_sse_conn}
          onChange={(e) => setDraft({ ...draft, max_tokens_per_sse_conn: num(e.target.value) })}
        />
      </td>
      <td className="px-4 py-2 text-right">
        <input
          type="text"
          className="w-20 rounded border border-ink/15 px-2 py-1 text-right text-sm"
          value={draft.overage_credit_price_usd}
          onChange={(e) => setDraft({ ...draft, overage_credit_price_usd: e.target.value })}
        />
      </td>
      <td className="px-4 py-2">
        <input
          type="checkbox"
          checked={draft.hidden}
          onChange={(e) => setDraft({ ...draft, hidden: e.target.checked })}
        />
      </td>
    </>
  );
}
